import React from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { Check, Monitor, Sun, Moon, type LucideIcon } from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Head from 'expo-router/head';
import { useTheme, SPACING, FONT_SIZES, TYPOGRAPHY, CONTENT_MAX_WIDTH } from '../../src/theme';
import { Header } from '@components/layout';
import { useThemeStore } from '../../src/store/themeStore';

type Mode = 'system' | 'light' | 'dark';

interface Option {
  id: Mode;
  label: string;
  description: string;
  icon: LucideIcon;
}

const OPTIONS: Option[] = [
  { id: 'system', label: 'System', description: 'Match your device settings', icon: Monitor },
  { id: 'light', label: 'Light', description: 'Always use light mode', icon: Sun },
  { id: 'dark', label: 'Dark', description: 'Always use dark mode', icon: Moon },
];

export default function AppearanceScreen() {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { mode, setMode } = useThemeStore();

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <Head>
        <title>Appearance - Redi</title>
      </Head>
      <Header showBack showAvatar={false} />
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={[styles.content, { paddingBottom: insets.bottom + SPACING.xl }]}>
          <Text style={[styles.screenTitle, { color: colors.text }]}>Appearance</Text>
          <View style={[styles.section, { borderTopColor: colors.border }]}>
            {OPTIONS.map((option) => {
              const Icon = option.icon;
              const selected = mode === option.id;
              return (
                <TouchableOpacity
                  key={option.id}
                  style={[styles.row, { borderBottomColor: colors.border }]}
                  onPress={() => setMode(option.id)}
                  activeOpacity={0.7}
                  accessibilityRole="radio"
                  aria-checked={selected}
                  accessibilityLabel={option.label}>
                  {/* Bare icon, no tinted box — same rule as ListItem: an
                      identical green square on every row flattens hierarchy. */}
                  <Icon size={18} color={colors.textSecondary} style={styles.icon} />
                  <View style={styles.textContainer}>
                    <Text style={[styles.label, { color: colors.text }]}>{option.label}</Text>
                    <Text style={[styles.description, { color: colors.textSecondary }]}>
                      {option.description}
                    </Text>
                  </View>
                  {selected && <Check size={20} color={colors.text} />}
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  scrollView: { flex: 1 },
  scrollContent: { alignItems: 'center' },
  content: {
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH,
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.sm,
  },
  screenTitle: {
    ...TYPOGRAPHY.screenTitle,
    marginBottom: SPACING.lg,
  },
  // Hairline rows, not a filled panel (mockup `.rowlink`).
  section: {
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: SPACING.smd,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  icon: {
    marginRight: SPACING.smd,
  },
  textContainer: {
    flex: 1,
  },
  label: {
    ...TYPOGRAPHY.h4,
    fontFamily: TYPOGRAPHY.label.fontFamily,
    fontSize: FONT_SIZES.md,
  },
  description: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 2,
  },
});
